import moment from 'moment-timezone';
export const caseMapperColumns = {
    migratedId: {
        map: ['Migrated Journal Number', 'Migreret Journalnummer'],
        parser: (val) => stringParser(val),
        validator: (val) => stringValidation(val, { nullable: true }),
    },
    caseSeqId: {
        map: ['Case Id', 'Sagsnummer'],
        parser: (val) => numberParser(val),
        validator: (val) => numberValidation(val, true),
    },
    caseReferenceId: {
        map: ['Case Reference Id', 'Sagsreferencenummer'],
        parser: (val) => stringParser(val),
        validator: (val) => stringValidation(val, { nullable: true }),
    },
};
export const dateValidation = (date, nullable) => {
    // Return if null
    if (!date && nullable) {
        return [];
    }
    // Check if data is null
    if (!date && !nullable) {
        return ['Date is null'];
    }
    // Check if date is valid
    if (isNaN(new Date(date).getTime())) {
        return ['Could not parse date'];
    }
    if (new Date(date).getTime() < -2208988800000 || // Year 1900
        new Date(date).getTime() > 2997648000000 // Year 2065
    ) {
        return ['Date unrealistic'];
    }
    return [];
};
export const numberValidation = (n, nullable) => {
    if (typeof n === 'number') {
        return [];
    }
    if (nullable && !n) {
        return [];
    }
    return ['Value is not a number'];
};
export const arrayValidation = (n, nullable) => {
    if (typeof n === 'object' && Array.isArray(n)) {
        return [];
    }
    if (nullable && !n) {
        return [];
    }
    return ['Value is not a number'];
};
export const booleanValidation = (b, nullable) => {
    if (typeof b === 'boolean') {
        return [];
    }
    if (nullable && !b) {
        return [];
    }
    return ['Value neither true or false'];
};
export const stringValidation = (s, c) => {
    if (!s && c?.nullable) {
        return [];
    }
    if (!c?.nullable && !s) {
        return ['Value is missing'];
    }
    if (typeof s !== 'string') {
        return ['Value not string'];
    }
    if (typeof c?.maxLength === 'number' && s && s.length > c?.maxLength) {
        return [`String '${s}' is too long. Max length is ${c?.maxLength}`];
    }
    if (typeof c?.minLength === 'number' && s && s.length < c?.minLength) {
        return [`String '${s}' is too short. Min length is ${c?.minLength}`];
    }
    if (c?.restrictedPossibilities) {
        const stringRestrictedPossibilities = c?.restrictedPossibilities.filter((l) => typeof l === 'string');
        const stringRestrictedPossibilitiesWithError = !stringRestrictedPossibilities.includes(s);
        const functionRestrictedPossibilities = c?.restrictedPossibilities.filter((l) => typeof l === 'function');
        const functionRestrictedPossibilitiesWithErrors = functionRestrictedPossibilities.filter((test) => typeof test === 'function' && !test(s));
        if ((functionRestrictedPossibilitiesWithErrors.length !== 0 ||
            functionRestrictedPossibilities.length === 0) &&
            stringRestrictedPossibilitiesWithError) {
            const possibleStrings = c?.restrictedPossibilities.filter((l) => typeof l === 'string');
            return [
                `String '${s}' not allowed${possibleStrings.length > 0
                    ? `. These are valid: ${possibleStrings
                        .map((s) => `'${s}'`)
                        .join(', ')}.`
                    : ''}`,
            ];
        }
    }
    return [];
};
export const excelDateParser = (value, options) => {
    if (value == null || (typeof value === 'string' && value.trim() === '')) {
        return null;
    }
    const serial = typeof value === 'number'
        ? value
        : typeof value === 'string' && /^\d+(?:\.\d+)?$/.test(value.trim())
            ? Number(value)
            : undefined;
    let date;
    if (serial !== undefined) {
        const utc = new Date(Math.round((serial - 25569) * 86400000));
        date = Number.isNaN(utc.getTime())
            ? utc
            : moment.tz(utc.toISOString().slice(0, -1), options.timezone).toDate();
    }
    else if (value instanceof Date) {
        date = value;
    }
    else if (typeof value === 'string') {
        date = moment
            .tz(value.trim(), [
            moment.ISO_8601,
            'DD/MM/YYYY',
            'DD.MM.YYYY',
            'DD-MM-YYYY',
            'DD/MM/YYYY HH:mm',
            'DD/MM/YYYY HH:mm:ss',
            'DD.MM.YYYY HH:mm',
            'DD.MM.YYYY HH:mm:ss',
        ], true, options.timezone)
            .toDate();
    }
    else {
        date = new Date(NaN);
    }
    return options.rawDate
        ? date
        : Number.isNaN(date.getTime())
            ? 'Invalid Date'
            : date.toISOString();
};
export const isUUID = (s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);
export const moneyParser = (n, c) => {
    const isNotNaN = !isNaN(Math.round(parseFloat(`${n}`.trim())) * 100);
    const num = parseFloat(`${n}`.trim());
    return isNotNaN && c?.allowZero
        ? Math.round(num * 100)
        : num
            ? Math.round(num * 100)
            : null;
};
export const numberParser = (n, c) => {
    const value = parseFloat(`${n}`.trim());
    if (Number.isNaN(value) || (value === 0 && c?.allowZero !== true)) {
        return null;
    }
    return c?.float === true ? value : Math.round(value);
};
export const arrayParser = (n) => (n ? n.split(',') : null);
export const percentageParser = (n, asFraction = false) => {
    const value = numberParser(typeof n === 'string' ? n.replace(',', '.') : n, {
        float: true,
        allowZero: true,
    });
    return value === null
        ? null
        : Math.round(value * (asFraction ? 1000000 : 10000)) / 1000000;
};
export const booleanParser = (b) => {
    if ((typeof b === 'string' && b.toLowerCase() == 'false') ||
        (typeof b === 'string' && b.toLowerCase() == 'falsk') ||
        b == '0' ||
        b == 0 ||
        b == false ||
        (typeof b === 'string' && b.toLowerCase() == 'nej') ||
        (typeof b === 'string' && b.toLowerCase() == 'no')) {
        return false;
    }
    else if ((typeof b === 'string' && b.toLowerCase() === 'true') ||
        (typeof b === 'string' && b.toLowerCase() === 'sand') ||
        b === '1' ||
        b === 1 ||
        b === true ||
        (typeof b === 'string' && b.toLowerCase() === 'yes') ||
        (typeof b === 'string' && b.toLowerCase() === 'ja')) {
        return true;
    }
    else {
        return undefined;
    }
};
export const stringParser = (s) => (s ? `${s}`.trim() : s);
export const cprParser = (value) => {
    const cpr = stringParser(value);
    return typeof cpr === 'string'
        ? cpr.replace(/^(\d{6})-(\d{4})$/, '$1$2')
        : cpr;
};
const keysToBeExcluded = ['rowValidation'];
const mapper = (handler, sheet, importMap) => {
    const map = Object.entries(handler).reduce((acc, [key, value]) => {
        if (keysToBeExcluded.includes(key)) {
            return acc;
        }
        // @ts-ignore
        acc[key] = (importMap && importMap[key]) || value.map;
        return acc;
    }, {});
    const matchMap = Object.entries(map).reduce((acc, [key, strings]) => {
        for (let i = 0; i < sheet.columns.length; i++) {
            const k = sheet.columns[i];
            if (strings.includes(k)) {
                acc[key] = k;
                return acc;
            }
        }
        acc[key] = null;
        return acc;
    }, {});
    const mM = Object.entries(matchMap);
    return {
        matchMap,
        content: sheet.rows.map((row) => mM.reduce((acc, [key, rowKey]) => {
            acc[key] = row[`${rowKey}`.toLowerCase()];
            return acc;
        }, {})),
    };
};
export const getNotMappedColumns = (rawWorkbook, sheetGetter, handler, importMap) => {
    const rawSheet = sheetGetter(rawWorkbook);
    if (!rawSheet) {
        return [];
    }
    const mapped = mapper(handler, rawSheet, importMap);
    return rawSheet.columns.filter((column) => !Object.values(mapped.matchMap).includes(column));
};
export const sheet = (handler, sheetGetter) => (workbook, parserOptions, importMap) => {
    const sheet = sheetGetter(workbook);
    if (!sheet) {
        return undefined;
    }
    const fixedValues = parserOptions.fixedValues ?? {};
    const fixedFields = Object.keys(fixedValues).filter((key) => key in handler);
    const mapped = mapper(handler, sheet, {
        ...importMap,
        ...Object.fromEntries(Object.keys(fixedValues).map((key) => [key, []])),
    });
    let validation = [];
    const parsedAndValidatedData = mapped.content.map((row, i) => Object.entries(row).reduce((acc, [key, data]) => {
        const field = handler[key];
        const fixedValue = Reflect.get(fixedValues, key);
        const parsed = field.parser(fixedValue === undefined ? data : fixedValue, {
            ...parserOptions,
            percentageAsFraction: fixedValue === undefined &&
                (sheet.percentageColumns?.[i]?.includes(mapped.matchMap[key] ?? '') ??
                    false),
        });
        const invertSign = parserOptions.fieldOptions?.[key]?.invertSign ??
            field.defaultInvertSign ??
            false;
        const d = (field.format === 'money' || field.format === 'number') &&
            invertSign &&
            typeof parsed === 'number' &&
            parsed !== 0
            ? -parsed
            : parsed;
        const cellValidation = handler[key].validator(d, parserOptions);
        if (cellValidation.length !== 0) {
            validation = validation.concat([
                {
                    index: i,
                    columnName: mapped.matchMap[key],
                    key,
                    problems: cellValidation,
                },
            ]);
        }
        acc[key] = {
            data: d,
            validation: cellValidation,
        };
        return acc;
    }, {}));
    if (handler.rowValidation) {
        for (let i = 0; i < parsedAndValidatedData.length; i++) {
            const row = parsedAndValidatedData[i];
            for (let f = 0; f < handler.rowValidation.length; f++) {
                const validator = handler.rowValidation[f];
                const problems = validator(row);
                if (problems.length !== 0) {
                    validation = validation.concat([
                        {
                            index: i,
                            problems,
                            rowValidation: true,
                        },
                    ]);
                }
            }
        }
    }
    return {
        validation,
        content: parsedAndValidatedData,
        ...(fixedFields.length === 0 ? {} : { fixedFields }),
        columns: sheet.columns,
        matchMap: mapped.matchMap,
        name: sheet.name,
        rowNumbers: sheet.rowNumbers,
    };
};
//# sourceMappingURL=helpers.js.map